import { useEffect, useState } from 'react';
import { CornerUpLeft, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Check, Lightbulb, MessageSquareText } from 'lucide-react';
import api from '../../services/api';

/** Libellés courts des domaines pour les vignettes (mêmes que TrialIdeas). */
const SHORT = {
    dgfip: 'DGFiP', istqb: 'ISTQB', atsem: 'ATSEM', crpe: 'CRPE',
    territorial: 'Collectivités', logique: 'Psychotechniques', culture: 'Culture générale',
};

/**
 * « Besoin d'inspiration ? » dans la Génération IA de la gestion des sujets : vignettes de domaine (celui
 * de la personne présélectionné), puis quelques idées de consignes ; « Reprendre cette idée » remplit le
 * titre et les consignes (onUseIdea). Une idée issue d'un exemple relu montre aussi un extrait du résultat.
 * Données : custom/prompt-ideas.php (fichier backend/data/prompt-ideas.json).
 */
export default function PromptIdeas({ disabled = false, defaultOpen = true, onUseIdea }) {
    const [ideas, setIdeas] = useState([]);
    const [domains, setDomains] = useState([]);
    const [domain, setDomain] = useState(null);
    const [ideaId, setIdeaId] = useState(null);
    const [open, setOpen] = useState(defaultOpen);
    const [showResult, setShowResult] = useState(false);
    const [qi, setQi] = useState(0);
    const [showExpl, setShowExpl] = useState(false);
    const [used, setUsed] = useState(null);

    useEffect(() => {
        let alive = true;
        api.get('/custom/prompt-ideas.php')
            .then(({ data }) => {
                if (!alive) return;
                const d = data.data || {};
                setIdeas(d.ideas || []);
                setDomains(d.domains || []);
                setDomain(d.default_domain || null);
            })
            .catch(() => {});
        return () => { alive = false; };
    }, []);

    const inDomain = ideas.filter(i => i.domain === domain);
    const idea = inDomain.find(i => i.id === ideaId) || inDomain[0] || null;
    useEffect(() => { setShowResult(false); setQi(0); setShowExpl(false); }, [idea?.id]);
    if (!ideas.length || !idea) return null;
    const q = idea.preview ? (idea.preview[qi] || idea.preview[0]) : null;

    return (
        <section className="trial3-ideas pideas">
            <button type="button" className="pideas__head" onClick={() => setOpen(o => !o)} aria-expanded={open}>
                <Lightbulb size={16} />
                <span className="trial3-ideas__title">Besoin d’inspiration ?</span>
                <span className="pideas__sub">Reprenez une idée, puis adaptez-la à vos révisions.</span>
                {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>

            {open && <>
                {domains.length > 1 && (
                    <div className="trial3-scopes" role="tablist" aria-label="Domaine">
                        {domains.map(d => (
                            <button key={d.key} type="button" role="tab" aria-selected={domain === d.key}
                                className={`trial3-scope${d.image ? ' has-image' : ''}${domain === d.key ? ' is-active' : ''}`}
                                style={{
                                    ...(d.color ? { '--scope-color': d.color } : {}),
                                    ...(d.image ? { backgroundImage: `url(${d.image})` } : {}),
                                }}
                                onClick={() => { setDomain(d.key); setIdeaId(null); }} disabled={disabled}>
                                <span>{SHORT[d.key] || d.label}</span>
                            </button>
                        ))}
                    </div>
                )}

                <div className="pideas__pills" role="list">
                    {inDomain.map(i => (
                        <button key={i.id} type="button" role="listitem" className={`pideas__pill${i.id === idea.id ? ' is-active' : ''}`}
                            onClick={() => setIdeaId(i.id)} disabled={disabled}>
                            {i.label}
                        </button>
                    ))}
                </div>

                <div className="trial3-idea">
                    <span className="trial3-idea__label"><MessageSquareText size={14} /> {idea.label}</span>
                    <p>{idea.consignes}</p>
                    <div className="trial3-idea__actions">
                        <button type="button" className="trial3-btn-light" disabled={disabled}
                            onClick={() => { onUseIdea?.({ label: idea.label, consignes: idea.consignes }); setUsed(idea.id); }}>
                            {used === idea.id ? <><Check size={14} /> Idée reprise, à adapter</> : <><CornerUpLeft size={14} /> Reprendre cette idée</>}
                        </button>
                        {idea.preview && (
                            <button type="button" className="trial3-toggle" onClick={() => setShowResult(o => !o)}>
                                {showResult ? 'Masquer le résultat' : 'Voir un résultat'} {showResult ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                            </button>
                        )}
                    </div>
                </div>

                {showResult && q && (
                    <div className="trial3-result">
                        <div className="trial3-result__bar">
                            <span>Extrait d’un sujet généré puis relu</span>
                            <span className="trial3-result__nav">
                                <button type="button" onClick={() => setQi(i => Math.max(0, i - 1))} disabled={qi === 0} aria-label="Question précédente"><ChevronLeft size={14} /></button>
                                Extrait {qi + 1} / {idea.preview.length}
                                <button type="button" onClick={() => setQi(i => Math.min(idea.preview.length - 1, i + 1))} disabled={qi >= idea.preview.length - 1} aria-label="Question suivante"><ChevronRight size={14} /></button>
                            </span>
                        </div>
                        <p className="trial3-result__q">{q.enonce}</p>
                        <ol className="trial3-answers">
                            {Object.entries(q.reponses).map(([k, v], i) => (
                                <li key={k} className={showExpl && k === q.bonne_reponse ? 'is-good' : ''}>
                                    <span className="trial3-answers__letter">{showExpl && k === q.bonne_reponse ? <Check size={13} /> : 'ABCD'[i]}</span>{v}
                                </li>
                            ))}
                        </ol>
                        <button type="button" className="trial3-toggle" onClick={() => setShowExpl(v => !v)}>
                            {showExpl ? 'Masquer la correction' : 'Voir la correction'}
                        </button>
                        {showExpl && (
                            <div className="trial3-expl">
                                {String(q.explication).split(/\n\s*\n/).map((para, j) => (
                                    // Contenu relu, stocké côté serveur : seules les balises <strong> sont conservées.
                                    <p key={j} dangerouslySetInnerHTML={{ __html: para.replace(/<(?!\/?strong>)[^>]*>/g, '') }} />
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </>}
        </section>
    );
}
